export interface LocationInfo {
  country: string
  currency: 'INR' | 'USD'
  currencySymbol: '₹' | '$'
  isIndia: boolean
}

export interface PricingInfo {
  price: number
  currency: 'INR' | 'USD'
  currencySymbol: '₹' | '$'
  displayPrice: string
}

// Enhanced location detection with more Indian cities and regions
const INDIAN_LOCATIONS = [
  // Major cities
  'mumbai', 'delhi', 'bangalore', 'hyderabad', 'chennai', 'kolkata', 'pune', 'ahmedabad', 'jaipur', 'surat',
  'lucknow', 'kanpur', 'nagpur', 'indore', 'thane', 'bhopal', 'visakhapatnam', 'patna', 'vadodara', 'ghaziabad',
  'ludhiana', 'agra', 'nashik', 'faridabad', 'meerut', 'rajkot', 'kalyan', 'vasai', 'vashi', 'aurangabad',
  'noida', 'gurugram', 'gurgaon', 'greater noida', 'faridabad', 'ghaziabad', 'meerut', 'gurgaon', 'noida', 'faridabad',
  
  // State capitals and major cities
  'chandigarh', 'dehradun', 'shimla', 'srinagar', 'jammu', 'amritsar', 'jodhpur', 'udaipur', 'jabalpur', 'raipur',
  'ranchi', 'bhubaneswar', 'guwahati', 'imphal', 'aizawl', 'kohima', 'itanagar', 'gangtok', 'shillong', 'agartala',
  'port blair', 'kavaratti', 'daman', 'diu', 'dadra', 'nagar haveli', 'panaji', 'panjim', 'margao', 'mapusa',
  
  // Union Territories
  'new delhi', 'chandigarh', 'daman and diu', 'dadra and nagar haveli', 'lakshadweep', 'puducherry', 'andaman and nicobar',
  
  // Common Indian place indicators
  'india', 'indian', 'bharat', 'hindustan', 'desh', 'apna desh'
]

// Current currency conversion rates (updated for 2024-2025)
const CURRENCY_RATES = {
  INR_TO_USD: 0.012, // 1 INR = 0.012 USD (approximately 83 INR = 1 USD)
  USD_TO_INR: 83.0   // 1 USD = 83 INR
}

/**
 * Detect if a location is in India based on place of birth
 */
export function detectLocationFromBirthData(birthData?: {
  place_of_birth?: string
  placeOfBirth?: string
  latitude?: number
  longitude?: number
}): LocationInfo {
  if (!birthData) {
    return {
      country: 'Unknown',
      currency: 'INR',
      currencySymbol: '₹',
      isIndia: true // Default to India if no data
    }
  }

  // Handle both property names for compatibility
  const placeOfBirth = (birthData.place_of_birth || birthData.placeOfBirth || '').toLowerCase()
  
  // Check if place of birth contains Indian locations
  const isIndia = INDIAN_LOCATIONS.some(location => 
    placeOfBirth.includes(location.toLowerCase())
  )

  // Additional check: if coordinates are within India's rough boundaries
  if (birthData.latitude && birthData.longitude) {
    const lat = birthData.latitude
    const lon = birthData.longitude
    
    // Rough boundaries of India (approximate)
    if (lat >= 6.0 && lat <= 37.0 && lon >= 68.0 && lon <= 97.0) {
      return {
        country: 'India',
        currency: 'INR',
        currencySymbol: '₹',
        isIndia: true
      }
    }
  }

  // If place of birth contains Indian locations, assume India
  if (isIndia) {
    return {
      country: 'India',
      currency: 'INR',
      currencySymbol: '₹',
      isIndia: true
    }
  }

  // Default to international (USD)
  return {
    country: 'International',
    currency: 'USD',
    currencySymbol: '$',
    isIndia: false
  }
}

/**
 * Convert price from INR to USD
 */
export function convertINRtoUSD(inrPrice: number): number {
  return Math.round(inrPrice * CURRENCY_RATES.INR_TO_USD * 100) / 100
}

/**
 * Convert price from USD to INR
 */
export function convertUSDtoINR(usdPrice: number): number {
  return Math.round(usdPrice * CURRENCY_RATES.USD_TO_INR)
}

/**
 * Get location-based pricing information
 */
export function getLocationBasedPricing(
  inrPrice: number,
  birthData?: {
    place_of_birth?: string
    placeOfBirth?: string
    latitude?: number
    longitude?: number
  }
): PricingInfo {
  const locationInfo = detectLocationFromBirthData(birthData)
  
  if (locationInfo.isIndia) {
    return {
      price: inrPrice,
      currency: 'INR',
      currencySymbol: '₹',
      displayPrice: `₹${inrPrice}`
    }
  } else {
    const usdPrice = convertINRtoUSD(inrPrice)
    return {
      price: usdPrice,
      currency: 'USD',
      currencySymbol: '$',
      displayPrice: `$${usdPrice}`
    }
  }
}

/**
 * Format price with currency symbol
 */
export function formatPrice(price: number, currency: 'INR' | 'USD'): string {
  if (currency === 'INR') {
    return `₹${price}`
  } else {
    return `$${price}`
  }
}

/**
 * Get location display text
 */
export function getLocationDisplayText(birthData?: {
  place_of_birth?: string
  placeOfBirth?: string
  latitude?: number
  longitude?: number
}): string {
  const locationInfo = detectLocationFromBirthData(birthData)
  
  if (locationInfo.isIndia) {
    return '🇮🇳 India - Prices in Indian Rupees (₹)'
  } else {
    return '🌍 International - Prices in US Dollars ($)'
  }
}
